/**
 * AdjustmentsSection - Tab content for managing forecast adjustments
 * Shows list of adjustments with add/edit/delete functionality
 */

import { ownerWindowOf } from '../../utils/ownerWindow';
import React, { useRef, useState } from 'react';
import { Plus, Minus, ChevronDown, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react';
import { PanelSkeleton } from '../shared/Skeleton';
import toast from 'react-hot-toast';
import { useAdjustments, formatAdjustmentValue, formatMonthRange } from '../../hooks/useAdjustments';
import { AdjustmentCard } from './AdjustmentCard';
import { AddAdjustmentForm } from './AddAdjustmentForm';
import type { ForecastAccount } from '../../types';
import type { ForecastAdjustment, AdjustmentType } from '../../api';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrency as sharedFormatCurrency } from '../../../../utils/currency';

interface AdjustmentsSectionProps {
  account: ForecastAccount;
  versionId: number;
  locationId: number;
  isReadOnly?: boolean;
  selectedYear: number;
  isRecalculating?: boolean;
  currency?: string | null;
}

export function AdjustmentsSection({
  account,
  versionId,
  locationId,
  isReadOnly = false,
  selectedYear,
  isRecalculating = false,
  currency,
}: AdjustmentsSectionProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingAdjustment, setEditingAdjustment] = useState<ForecastAdjustment | null>(null);
  const [showExpired, setShowExpired] = useState(false);

  const {
    activeAdjustments,
    expiredAdjustments,
    activeCount,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    createAdjustment,
    updateAdjustment,
    deleteAdjustment,
    refetch,
    isExpiringSoon,
  } = useAdjustments({
    versionId,
    chartOfAccountId: account.chartOfAccountId,
    locationId,
    includeExpired: true,
    enabled: versionId > 0,
  });

  const handleCreate = async (values: {
    adjustmentType: AdjustmentType;
    value: number;
    description: string;
    startMonth: string;
    endMonth: string;
    expiresAt?: string;
  }) => {
    try {
      await createAdjustment(values);
      toast.success('Adjustment created');
      setIsAddingNew(false);
    } catch (err) {
      toast.error('Failed to create adjustment');
      throw err;
    }
  };

  const handleUpdate = async (id: number, values: {
    adjustmentType: AdjustmentType;
    value: number;
    description: string;
    startMonth: string;
    endMonth: string;
    expiresAt?: string;
  }) => {
    try {
      await updateAdjustment(id, values);
      toast.success('Adjustment updated');
      setEditingAdjustment(null);
    } catch (err) {
      toast.error('Failed to update adjustment');
      throw err;
    }
  };

  const handleDelete = async (id: number) => {
    if (!ownerWindowOf(rootRef.current).confirm('Are you sure you want to delete this adjustment?')) return;

    try {
      await deleteAdjustment(id);
      toast.success('Adjustment deleted');
    } catch (err) {
      toast.error('Failed to delete adjustment');
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="rn-sk-classic-only flex items-center justify-center py-8">
          <Loader2 size={24} className="animate-spin text-slate-400" />
        </div>
        <div className="rn-sk-inset-only"><PanelSkeleton items={3} /></div>
      </>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-600">Failed to load adjustments</p>
        <button
          onClick={() => refetch()}
          className="mt-2 text-xs text-red-600 underline hover:text-red-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-slate-700">Adjustments</h4>
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
              {activeCount} active
            </span>
          )}
        </div>
        {!isReadOnly && !isAddingNew && !editingAdjustment && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors"
            style={{ color: 'var(--lodge-orange-500)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-50)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = ''; }}
          >
            <Plus size={14} />
            Add
          </button>
        )}
      </div>

      {/* Info box */}
      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
        <p className="text-xs text-blue-700">
          Adjustments are applied <strong>after</strong> driver calculations.
          Flat amounts are added first, then percentages are applied to the result.
        </p>
      </div>

      {/* Add Form */}
      {isAddingNew && (
        <AddAdjustmentForm
          onSubmit={handleCreate}
          onCancel={() => setIsAddingNew(false)}
          isSubmitting={isCreating}
          selectedYear={selectedYear}
          currency={currency}
        />
      )}

      {/* Edit Form */}
      {editingAdjustment && (
        <AddAdjustmentForm
          adjustment={editingAdjustment}
          onSubmit={(values) => handleUpdate(editingAdjustment.id, values)}
          onCancel={() => setEditingAdjustment(null)}
          isSubmitting={isUpdating}
          selectedYear={selectedYear}
          currency={currency}
        />
      )}

      {/* Active Adjustments */}
      {!isAddingNew && !editingAdjustment && (
        <>
          {activeAdjustments.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              No active adjustments
            </div>
          ) : (
            <div className="space-y-2">
              {activeAdjustments.map((adjustment) => (
                <AdjustmentCard
                  key={adjustment.id}
                  adjustment={adjustment}
                  isExpiringSoon={isExpiringSoon(adjustment)}
                  currency={currency}
                  onEdit={() => setEditingAdjustment(adjustment)}
                  onDelete={() => handleDelete(adjustment.id)}
                  isReadOnly={isReadOnly}
                  isDeleting={isDeleting}
                />
              ))}
            </div>
          )}

          {/* Expired Adjustments */}
          {expiredAdjustments.length > 0 && (
            <div className="mt-4">
              <button
                onClick={() => setShowExpired(!showExpired)}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                {showExpired ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {expiredAdjustments.length} expired adjustment{expiredAdjustments.length !== 1 ? 's' : ''}
              </button>

              {showExpired && (
                <div className="mt-2 space-y-2 opacity-60">
                  {expiredAdjustments.map((adjustment) => (
                    <AdjustmentCard
                      key={adjustment.id}
                      adjustment={adjustment}
                      isExpired
                      currency={currency}
                      onEdit={() => setEditingAdjustment(adjustment)}
                      onDelete={() => handleDelete(adjustment.id)}
                      isReadOnly={isReadOnly}
                      isDeleting={isDeleting}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Calculation Preview */}
      {activeAdjustments.length > 0 && !isAddingNew && !editingAdjustment && (
        <CalculationPreview
          account={account}
          adjustments={activeAdjustments}
          selectedYear={selectedYear}
          isRecalculating={isRecalculating}
          currency={currency}
        />
      )}
    </div>
  );
}

/**
 * CalculationPreview - Shows breakdown of base + adjustments = final
 * Uses actual values from the API (baseAmount, forecast) to show accurate calculation
 */
interface CalculationPreviewProps {
  account: ForecastAccount;
  adjustments: ForecastAdjustment[];
  selectedYear: number;
  isRecalculating?: boolean;
  currency?: string | null;
}

function CalculationPreview({ account, adjustments, selectedYear, isRecalculating = false, currency }: CalculationPreviewProps) {
  // Determine the first applicable month from the adjustments (instead of hardcoding January)
  const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const monthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Find the earliest start month from active adjustments
  // Parse month directly from ISO string to avoid timezone off-by-one issues
  let previewMonthIndex = 0; // default to January (0-indexed)
  if (adjustments.length > 0) {
    let earliest = Infinity;
    for (const adj of adjustments) {
      if (adj.startMonth) {
        // Parse month from ISO string "YYYY-MM-..." directly (timezone-safe)
        const iso = typeof adj.startMonth === 'string' ? adj.startMonth : new Date(adj.startMonth).toISOString();
        const m = parseInt(iso.substring(5, 7), 10) - 1; // convert 1-indexed to 0-indexed
        if (m < earliest) {
          earliest = m;
        }
      }
    }
    if (earliest !== Infinity) {
      previewMonthIndex = earliest;
    }
  }

  const previewMonthKey = monthKeys[previewMonthIndex];
  const previewMonthLabel = monthLabels[previewMonthIndex];

  // Get the preview month's values from monthlyExpanded which has the actual calculated values
  const monthExpanded = account.monthlyExpanded?.[previewMonthKey];

  // Use baseAmount (pre-adjustment driver value) and forecast (final value with adjustments)
  const baseValue = monthExpanded?.baseAmount ?? account.monthlyValues[previewMonthKey] ?? 0;
  const finalValue = monthExpanded?.forecast ?? account.monthlyValues[previewMonthKey] ?? 0;
  const adjustmentsTotal = monthExpanded?.adjustmentsTotal ?? 0;

  const flatAdj = adjustments.filter(a => a.adjustmentType === 'FLAT');
  const pctAdj = adjustments.filter(a => a.adjustmentType === 'PERCENT');

  const formatCurrency = (value: number) =>
    sharedFormatCurrency(value, currency);

  // Shimmer placeholder for values during recalculation
  const ShimmerValue = () => (
    <span className="inline-block h-4 w-20 bg-gradient-to-r from-emerald-200 via-emerald-100 to-emerald-200 rounded animate-pulse"
      style={{
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s ease-in-out infinite',
      }}
    />
  );

  return (
    <div className="relative p-3 bg-emerald-50 border border-emerald-100 rounded-lg overflow-hidden">
      {/* Recalculating overlay with shimmer effect */}
      {isRecalculating && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s ease-in-out infinite',
          }}
        />
      )}

      <h4 className="text-xs font-medium text-emerald-800 mb-2 flex items-center gap-2">
        {previewMonthLabel} {selectedYear} Preview
        {isRecalculating && (
          <span className="flex items-center gap-1" style={{ color: 'var(--lodge-orange-500)' }}>
            <Loader2 size={12} className="animate-spin" />
            <span className="text-[10px]">Recalculating...</span>
          </span>
        )}
      </h4>
      <div className="space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-emerald-700">Driver Value</span>
          <span className="font-mono text-emerald-900">
            {isRecalculating ? <ShimmerValue /> : formatCurrency(baseValue)}
          </span>
        </div>
        {flatAdj.map((a) => (
          <div key={a.id} className="flex justify-between">
            <span className="text-emerald-700">+ {a.description || 'Flat adjustment'}</span>
            <span className="font-mono text-emerald-900">
              {formatAdjustmentValue('FLAT', a.value, currency)}
            </span>
          </div>
        ))}
        {pctAdj.map((a) => (
          <div key={a.id} className="flex justify-between">
            <span className="text-emerald-700">+ {a.value}%</span>
            <span className="font-mono text-emerald-900">
              {a.value}%
            </span>
          </div>
        ))}
        <div className="border-t border-emerald-200 pt-1 flex justify-between font-medium">
          <span className="text-emerald-800">Forecast</span>
          <span className="font-mono text-emerald-900">
            {isRecalculating ? (
              <span className="flex items-center gap-1">
                = <ShimmerValue />
              </span>
            ) : (
              `= ${formatCurrency(finalValue)}`
            )}
          </span>
        </div>
      </div>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

export default AdjustmentsSection;
